import React, { useEffect, useState, useRef } from 'react';
import DoctorInfoService from '../doctorInfo/doctorInfoService';
import DoctorClockService from '../ClockInService/DoctorClockService';
import OfficeService from '../office/officeService';
import ImageService from '../images/imageService';
import './drDash.css';
import { Pencil } from 'lucide-react';

const DoctorDashboard = () => {
  const [doctor, setDoctor] = useState(null);
  const [doctorId, setDoctorId] = useState(null);
  const [officeName, setOfficeName] = useState('');
  const [todayLog, setTodayLog] = useState(null);
  const [logError, setLogError] = useState('');
  const [clockedIn, setClockedIn] = useState(false);
  const [onBreak, setOnBreak] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [editedDoctor, setEditedDoctor] = useState({});
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [loading, setLoading] = useState(true);

  const fileInputRef = useRef(null);

  const doctorService = new DoctorInfoService();
  const clockService = new DoctorClockService();

  const normalizeDoctorForEdit = (doctorData) => {
    if (!doctorData) return {};

    return {
      ...doctorData,
      firstname: doctorData.firstname || doctorData.firstName || '',
      lastname: doctorData.lastname || doctorData.lastName || '',
      firstName: doctorData.firstName || doctorData.firstname || '',
      lastName: doctorData.lastName || doctorData.lastname || '',
      bio: doctorData.bio || '',
      specialty: doctorData.specialty || '',
      doctorPicture: doctorData.doctorPicture || '',
      username: doctorData.username || '',
      officeId: doctorData.officeId || 0,
    };
  };

  const getDoctorFirstName = (doctorData) => {
    return doctorData?.firstname || doctorData?.firstName || '';
  };

  const getDoctorLastName = (doctorData) => {
    return doctorData?.lastname || doctorData?.lastName || '';
  };

  const getErrorMessage = (err, fallback = 'Something went wrong.') => {
    if (!err) return fallback;

    if (typeof err === 'string') return err;

    if (err.response?.data) {
      if (typeof err.response.data === 'string') {
        return err.response.data;
      }

      return err.response.data.error || err.response.data.message || fallback;
    }

    return err.message || fallback;
  };

  const showSuccess = (msg) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const getLocation = () =>
    new Promise((resolve) => {
      if (typeof navigator === 'undefined' || !navigator.geolocation) {
        console.warn('Geolocation not supported in this environment.');
        resolve(null);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (pos) => {
          resolve({
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
          });
        },
        (err) => {
          console.warn('Geolocation error:', err);
          resolve(null);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
        }
      );
    });

  const refreshTodayLog = async (idOverride) => {
    const idToUse = idOverride || doctorId;

    if (!idToUse) return null;

    try {
      const log = await clockService.getTodayLog(idToUse);
      setTodayLog(log);
      setLogError('');

      const hasOpenBreak = log?.breaks?.some((b) => b.breakEnd == null);
      setOnBreak(Boolean(hasOpenBreak));

      return log;
    } catch (err) {
      const message = getErrorMessage(err, 'Unable to load today’s clock log.');
      setLogError(message);
      return null;
    }
  };

  const loadDashboard = async () => {
    const storedDoctorId = localStorage.getItem('doctorId');

    setDoctorId(storedDoctorId);

    if (!storedDoctorId) {
      setLoading(false);
      console.error('No doctorId found in localStorage.');
      return;
    }

    try {
      setLoading(true);

      const doctorData = await doctorService.getDoctorById(storedDoctorId);
      const normalizedDoctor = normalizeDoctorForEdit(doctorData);

      setDoctor(normalizedDoctor);
      setEditedDoctor(normalizedDoctor);

      if (normalizedDoctor.officeId) {
        try {
          const office = await new OfficeService().getOfficeNameById(
            normalizedDoctor.officeId
          );
          setOfficeName(office);
        } catch (officeErr) {
          console.error('Error loading office name:', officeErr);
          setOfficeName('');
        }
      }

      try {
        const status = await clockService.getClockStatus(storedDoctorId);
        setClockedIn(Boolean(status?.clockedIn));
      } catch (statusErr) {
        console.error('Error loading clock status:', statusErr);
      }

      await refreshTodayLog(storedDoctorId);
    } catch (err) {
      console.error('Error loading doctor/dashboard:', err);
      alert('❌ ' + getErrorMessage(err, 'Error loading doctor dashboard.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) {
      setSelectedImage(null);
      return;
    }

    if (!file.type.startsWith('image/')) {
      alert('Please select a valid image file.');
      e.target.value = '';
      setSelectedImage(null);
      return;
    }

    setSelectedImage(file);
  };

  const handleUpdateProfile = async () => {
    try {
      const firstName = (
        editedDoctor.firstname ||
        editedDoctor.firstName ||
        ''
      ).trim();

      const lastName = (
        editedDoctor.lastname ||
        editedDoctor.lastName ||
        ''
      ).trim();

      if (!firstName) {
        alert('First name is required.');
        return;
      }

      if (!lastName) {
        alert('Last name is required.');
        return;
      }

      let imageFileName = doctor?.doctorPicture || '';

      if (selectedImage instanceof File) {
        const extension = selectedImage.name.includes('.')
          ? selectedImage.name.substring(selectedImage.name.lastIndexOf('.'))
          : '';

        const safeFileName = `doctor_${doctorId}_${Date.now()}${extension}`;

        await ImageService.uploadImage(selectedImage, safeFileName, 'doctor');
        imageFileName = safeFileName;
      }

      const detailsToUpdate = {
        firstName,
        lastName,
        bio: editedDoctor.bio?.trim() || '',
        doctorPicture: imageFileName,
      };

      await doctorService.updateDoctorDetails(doctorId, null, detailsToUpdate);

      const updated = await doctorService.getDoctorById(doctorId);
      const normalizedUpdated = normalizeDoctorForEdit(updated);

      setDoctor(normalizedUpdated);
      setEditedDoctor(normalizedUpdated);
      setEditMode(false);
      setSelectedImage(null);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      showSuccess('Profile updated.');
    } catch (err) {
      console.error('Profile update failed:', err);
      alert('❌ ' + getErrorMessage(err, 'Profile update failed.'));
    }
  };

  const handleUpdateCredentials = async () => {
    try {
      const oldPwd = oldPassword.trim();
      const newPwd = newPassword.trim();

      if (!oldPwd || !newPwd) {
        alert('Please enter your current password and a new password.');
        return;
      }

      await doctorService.changePasswordSelf(oldPwd, newPwd);

      setOldPassword('');
      setNewPassword('');
      showSuccess('Password updated successfully.');
    } catch (err) {
      console.error('Password update failed:', err);
      alert('❌ ' + getErrorMessage(err, 'Password update failed.'));
    }
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setEditedDoctor(normalizeDoctorForEdit(doctor));
    setOldPassword('');
    setNewPassword('');
    setSelectedImage(null);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const ClockFace = () => {
    const [time, setTime] = useState(new Date());

    useEffect(() => {
      const interval = setInterval(() => setTime(new Date()), 1000);
      return () => clearInterval(interval);
    }, []);

    const hourDeg = (time.getHours() % 12) * 30 + time.getMinutes() * 0.5;
    const minuteDeg = time.getMinutes() * 6;
    const secondDeg = time.getSeconds() * 6;

    const handleClockIn = async () => {
      try {
        const coords = await getLocation();
        await clockService.clockIn(doctorId, coords);
        setClockedIn(true);
        setOnBreak(false);
        showSuccess('Clocked in.');
        await refreshTodayLog();
      } catch (err) {
        console.error('Clock in failed:', err);
        alert('❌ ' + getErrorMessage(err, 'Clock in failed.'));
      }
    };

    const handleStartBreak = async () => {
      try {
        const coords = await getLocation();
        await clockService.startBreak(doctorId, coords);
        setOnBreak(true);
        await refreshTodayLog();
      } catch (err) {
        console.error('Start break failed:', err);
        alert('❌ ' + getErrorMessage(err, 'Start break failed.'));
      }
    };

    const handleEndBreak = async () => {
      try {
        const coords = await getLocation();
        await clockService.endBreak(doctorId, coords);
        setOnBreak(false);
        await refreshTodayLog();
      } catch (err) {
        console.error('End break failed:', err);
        alert('❌ ' + getErrorMessage(err, 'End break failed.'));
      }
    };

    const handleClockOut = async () => {
      try {
        const coords = await getLocation();
        await clockService.clockOut(doctorId, coords);
        setClockedIn(false);
        setOnBreak(false);
        showSuccess('Clocked out.');
        await refreshTodayLog();
      } catch (err) {
        console.error('Clock out failed:', err);
        alert('❌ ' + getErrorMessage(err, 'Clock out failed.'));
      }
    };

    return (
      <div className="analog-clock">
        {[...Array(12)].map((_, i) => (
          <div key={i + 1} className="clock-number" data-number={i + 1}>
            <span>{i + 1}</span>
          </div>
        ))}

        <div
          className="clock-hand hour"
          style={{ transform: `rotate(${hourDeg}deg)` }}
        />
        <div
          className="clock-hand minute"
          style={{ transform: `rotate(${minuteDeg}deg)` }}
        />
        <div
          className="clock-hand second"
          style={{ transform: `rotate(${secondDeg}deg)` }}
        />

        <div className="clock-controls">
          {!clockedIn && (
            <button
              className="clock-button"
              onClick={handleClockIn}
              disabled={!doctorId}
            >
              Clock In
            </button>
          )}

          {clockedIn && !onBreak && (
            <button
              className="clock-button"
              onClick={handleStartBreak}
              disabled={!doctorId}
            >
              Break
            </button>
          )}

          {onBreak && (
            <button
              className="clock-button"
              onClick={handleEndBreak}
              disabled={!doctorId}
            >
              Resume
            </button>
          )}

          {clockedIn && (
            <button
              className="clock-button"
              onClick={handleClockOut}
              disabled={!doctorId}
            >
              Clock Out
            </button>
          )}
        </div>
      </div>
    );
  };

  const profileImageSrc = selectedImage
    ? URL.createObjectURL(selectedImage)
    : doctor?.doctorPicture
    ? ImageService.getImageUrl('doctor', doctor.doctorPicture)
    : '/images/doctor/default.jpg';

  if (loading) {
    return (
      <div className="container">
        <h1 className="title">Doctor Dashboard</h1>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  if (!doctorId) {
    return (
      <div className="container">
        <h1 className="title">Doctor Dashboard</h1>
        <p style={{ color: 'red' }}>
          No doctor ID was found. Please log out and log back in as a doctor.
        </p>
      </div>
    );
  }

  return (
    <div className="container">
      <h1 className="title">Doctor Dashboard</h1>

      {successMessage && (
        <div className="success-message">{successMessage}</div>
      )}

      <div className="doctor-dashboard-layout">
        <div className="dashboard-left">
          <div className="doctor-card">
            <div
              className="edit-icon"
              onClick={() => setEditMode((prev) => !prev)}
              title="Edit profile"
            >
              <Pencil size={18} />
            </div>

            <img
              src={profileImageSrc}
              alt="Profile"
              className="profile-picture"
              onError={(e) => {
                if (!e.target.dataset.fallback) {
                  e.target.src = '/images/doctor/default.jpg';
                  e.target.dataset.fallback = 'true';
                }
              }}
            />

            <div className="doctor-info">
              {editMode ? (
                <>
                  <p className="edit-helper-text">
                    You can update your profile info and password here.
                    <br />
                    To change your password, you{' '}
                    <strong>must enter your current password</strong> and then
                    choose a new one. If you don’t remember your current
                    password, please contact an administrator or manager to reset
                    it for you.
                  </p>

                  <label>First Name</label>
                  <input
                    className="edit-input"
                    value={editedDoctor.firstname || editedDoctor.firstName || ''}
                    onChange={(e) =>
                      setEditedDoctor({
                        ...editedDoctor,
                        firstname: e.target.value,
                        firstName: e.target.value,
                      })
                    }
                  />

                  <label>Last Name</label>
                  <input
                    className="edit-input"
                    value={editedDoctor.lastname || editedDoctor.lastName || ''}
                    onChange={(e) =>
                      setEditedDoctor({
                        ...editedDoctor,
                        lastname: e.target.value,
                        lastName: e.target.value,
                      })
                    }
                  />

                  <label>Specialty (Not Editable)</label>
                  <input
                    className="edit-input"
                    disabled
                    value={editedDoctor.specialty || ''}
                  />

                  <label>Bio</label>
                  <textarea
                    className="edit-textarea"
                    value={editedDoctor.bio || ''}
                    onChange={(e) =>
                      setEditedDoctor({
                        ...editedDoctor,
                        bio: e.target.value,
                      })
                    }
                  />

                  <label>Username (Not Editable Here)</label>
                  <input
                    className="edit-input"
                    value={doctor?.username || ''}
                    disabled
                  />

                  <label>Upload Profile Picture</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    ref={fileInputRef}
                  />

                  <div className="password-section">
                    <h4>Change Password</h4>

                    <small className="password-helper">
                      For security reasons, you must enter your current password
                      before setting a new one. If you forgot your current
                      password, please ask an admin or manager to reset it from
                      the staff directory.
                    </small>

                    <label>Current Password</label>
                    <input
                      className="edit-input"
                      type="password"
                      value={oldPassword}
                      onChange={(e) => setOldPassword(e.target.value)}
                    />

                    <label>New Password</label>
                    <input
                      className="edit-input"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                    />
                  </div>

                  <div className="button-row">
                    <button
                      className="save-button"
                      onClick={handleUpdateProfile}
                    >
                      Save Profile Info
                    </button>

                    <button
                      className="password-button"
                      onClick={handleUpdateCredentials}
                    >
                      Update Password
                    </button>

                    <button
                      className="cancel-button"
                      onClick={handleCancelEdit}
                    >
                      Cancel
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <h2>
                    {getDoctorFirstName(doctor)} {getDoctorLastName(doctor)}
                  </h2>

                  <p>
                    <strong>Office:</strong> {officeName}
                  </p>

                  <p>
                    <strong>Specialty:</strong> {doctor?.specialty}
                  </p>

                  <p>
                    <strong>Bio:</strong> {doctor?.bio}
                  </p>

                  <p>
                    <strong>Username:</strong> {doctor?.username}
                  </p>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="dashboard-right">
          <ClockFace />

          <div className="clock-history">
            <h3>Today’s Clock Log</h3>

            {logError && <p style={{ color: 'red' }}>{logError}</p>}

            {!todayLog?.clockIn ? (
              <p>No log for today yet.</p>
            ) : (
              <table className="clock-table">
                <tbody>
                  <tr>
                    <td>Clock In:</td>
                    <td>{new Date(todayLog.clockIn).toLocaleTimeString()}</td>
                  </tr>

                  <tr>
                    <td>Clock Out:</td>
                    <td>
                      {todayLog.clockOut
                        ? new Date(todayLog.clockOut).toLocaleTimeString()
                        : '—'}
                    </td>
                  </tr>

                  <tr>
                    <td>Total Worked:</td>
                    <td>{todayLog.totalWorked} minutes</td>
                  </tr>

                  {todayLog.breaks?.length > 0 && (
                    <tr>
                      <td>Breaks:</td>
                      <td>
                        {todayLog.breaks.map((b, i) => (
                          <div key={i}>
                            {new Date(b.breakStart).toLocaleTimeString()} →{' '}
                            {b.breakEnd
                              ? new Date(b.breakEnd).toLocaleTimeString()
                              : 'Ongoing'}
                          </div>
                        ))}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DoctorDashboard;